import * as z from "zod/v4";
import { zPermissionKey } from "../features/permissions/permissions";
import { zCompetence } from "./competence";

export const zUserResponse = z.object({
  id: z.number(),
  name: z.string(),
  initials: z.string().optional().nullish(), // Legacy users might not have this
  firstName: z.string().optional().nullish(), // Legacy users might not have this
  lastName: z.string().optional().nullish(), // Legacy users might not have this
  emailAddress: z.email().optional().nullish(),
  disabled: z.boolean().nullish().default(false),
  competences: z.array(zCompetence).nullish().default([]),
  permissions: z
    .partialRecord(zPermissionKey, z.boolean().nullish())
    .nullish()
    .default({}),
  roleIds: z.array(z.number()).nullish().default([]),
});

export const zCurrentUser = zUserResponse.extend({
  allPermissions: z.array(zPermissionKey).nullish().default([]),
});

// Strict schema - for creating/updating users (enforces required fields)
export const zUserInput = z
  .object({
    id: z.number().optional(),
    name: z.string().min(1, "Användarnamn krävs"),
    initials: z.string().min(2, "Initialer krävs (minst 2 tecken)"),
    password: z
      .string()
      .min(8, "Lösenord måste vara minst 8 tecken")
      .optional(),
    passwordConfirmation: z.string().min(8).optional(),
    firstName: z.string().min(1, "Förnamn krävs"),
    lastName: z.string().min(1, "Efternamn krävs"),
    emailAddress: z.email("Ogiltig e-postadress").nullish(),
    disabled: z.boolean().nullish(),
    competences: z.array(zCompetence).nullish(),
    permissions: z
      .partialRecord(zPermissionKey, z.boolean().nullish())
      .nullish(),
    roleIds: z.array(z.number()).nullish(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "Lösenorden matchar inte",
    path: ["passwordConfirmation"],
  });

// Display-safe user schema (no sensitive fields)
export const zUserDisplay = z.object({
  id: z.number(),
  name: z.string(),
  initials: z.string().optional().nullish(), // Allow null for legacy users
  firstName: z.string().optional().nullish(), // Allow null for legacy users
  lastName: z.string().optional().nullish(), // Allow null for legacy users
  disabled: z.boolean().nullish().default(false),
  roleIds: z.array(z.number()).nullish().default([]),
});

// Type exports
export type UserResponse = z.infer<typeof zUserResponse>;
export type CurrentUser = z.infer<typeof zCurrentUser>;
export type UserInput = z.infer<typeof zUserInput>;
export type UserDisplay = z.infer<typeof zUserDisplay>;

export const validateUsername = (
  user: Pick<UserInput, "id" | "name">,
  users: readonly UserResponse[],
) => {
  const existingUser = users.find(
    (u) =>
      u.name?.toLowerCase() === user.name?.toLowerCase() && u.id !== user.id,
  );
  if (existingUser) {
    return {
      message: "Användarnamnet är upptaget",
    };
  }
  return undefined;
};

export const validateInitials = (
  user: Pick<UserInput, "id" | "initials">,
  users: readonly UserResponse[],
) => {
  const existingUser = users.find(
    (u) =>
      u.initials?.toLowerCase() === user.initials?.toLowerCase() &&
      u.id !== user.id,
  );
  if (existingUser) {
    return {
      message: "Initialerna används redan",
    };
  }
  return undefined;
};

export const displayName = (user: UserDisplay | undefined): string => {
  if (!user) {
    return "Okänd användare";
  }
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.name;
};
